import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api, fmtDate, hasRole, timeLeft } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHead } from "@/components/desk/DeskLayout";
import { StatusChip } from "@/components/desk/StatusChip";
import { TrendBadge } from "@/components/news/TrendBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const STATUSES = ["draft", "submitted", "approved", "scheduled", "published", "on_hold", "rejected", "withdrawn"];
const sel = "h-9 border border-zinc-300 bg-white px-2 text-sm rounded-sm";

export default function StoriesPage({ queue = false }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");
  const status = queue ? params.get("status") || "submitted" : params.get("status") || "";
  const trend = params.get("trend") || "";
  const category = params.get("category") || "";
  const source = params.get("source") || "";

  useEffect(() => {
    api.get("/desk/meta").then((r) => setCats(r.data.categories));
  }, []);

  useEffect(() => {
    api.get("/desk/stories", { params: { status: status || undefined, trend: trend || undefined, category: category || undefined, source: source || undefined, q: params.get("q") || undefined } })
      .then((r) => setRows(r.data));
  }, [status, trend, category, source, params]);

  const setP = (k, v) => {
    const p = new URLSearchParams(params);
    v ? p.set(k, v) : p.delete(k);
    setParams(p);
  };
  const catName = (id) => cats.find((c) => c.id === id)?.name_en || "—";
  const reporterOnly = !hasRole(user, "editor", "news_approver", "reporter_approver");

  return (
    <div data-testid={queue ? "approval-queue-page" : "stories-page"}>
      <PageHead title={queue ? "Approval queue" : reporterOnly ? "My stories" : "Stories"} sub={queue ? "Review submitted and on-hold stories. Stories can be in Tamil, English or both." : `${rows.length} stories`}>
        {hasRole(user, "editor", "reporter") && !queue && (
          <Button asChild className="rounded-sm" data-testid="stories-new-btn"><Link to="/desk/stories/new">New story</Link></Button>
        )}
      </PageHead>
      <div className="flex flex-wrap gap-2 mb-4" data-testid="stories-filters">
        {queue ? (
          <div className="flex gap-1">
            {["submitted", "on_hold", "approved", "scheduled"].map((s) => (
              <button key={s} data-testid={`queue-tab-${s}`} onClick={() => setP("status", s)}
                className={`h-9 px-3 text-sm border rounded-sm ${status === s ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`}>
                {s.replace("_", " ")}
              </button>
            ))}
          </div>
        ) : (
          <select data-testid="filter-status" className={sel} value={status} onChange={(e) => setP("status", e.target.value)}>
            <option value="">All statuses</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        )}
        <select data-testid="filter-trend" className={sel} value={trend} onChange={(e) => setP("trend", e.target.value)}>
          <option value="">All trends</option>
          <option value="breaking">Breaking</option><option value="hot">Hot</option><option value="regular">Regular</option>
        </select>
        <select data-testid="filter-category" className={sel} value={category} onChange={(e) => setP("category", e.target.value)}>
          <option value="">All categories</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name_en}</option>)}
        </select>
        {!reporterOnly && (
          <select data-testid="filter-source" className={sel} value={source} onChange={(e) => setP("source", e.target.value)}>
            <option value="">All sources</option><option value="staff">Staff</option><option value="reporter">External reporters</option>
          </select>
        )}
        <form onSubmit={(e) => { e.preventDefault(); setP("q", q); }} className="flex">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-zinc-400" />
            <Input data-testid="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search headlines" className="h-9 pl-8 w-56 rounded-sm bg-white" />
          </div>
        </form>
      </div>
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <Table data-testid="stories-table">
          <TableHeader>
            <TableRow>
              <TableHead>Headline</TableHead><TableHead>Status</TableHead><TableHead>Trend</TableHead>
              <TableHead>Category</TableHead><TableHead>Author</TableHead><TableHead>Updated</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-10" data-testid="stories-empty">No stories match these filters.</TableCell></TableRow>
            )}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`story-row-${r.id}`} className="cursor-pointer" onClick={() => nav(`/desk/stories/${r.id}`)}>
                <TableCell className="max-w-md">
                  <p className="font-semibold leading-snug lang-ta">{r.en?.headline || r.ta?.headline || <span className="text-zinc-400">Untitled</span>}</p>
                  <p className="text-[11px] text-zinc-500 mt-0.5 flex gap-1">
                    {r.en?.headline && <span className="border border-zinc-300 px-1">EN</span>}
                    {r.ta?.headline && <span className="border border-zinc-300 px-1">TA</span>}
                    {r.en?.headline && r.ta?.headline && <span className="lang-ta truncate">{r.ta.headline}</span>}
                  </p>
                  {r.status === "rejected" && r.feedback && <p className="text-xs text-orange-700 mt-1">Feedback: {r.feedback}</p>}
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status} testId={`row-status-${r.id}`} />
                  {r.status === "scheduled" && <p className="text-[11px] text-purple-700 mt-1 font-mono-meta">{fmtDate(r.scheduled_at)}</p>}
                </TableCell>
                <TableCell>
                  <TrendBadge trend={r.trend_type} testId={`row-trend-${r.id}`} />
                  {r.status === "published" && r.trend_type !== "regular" && r.trend_expires_at && (
                    <p className="text-[11px] text-zinc-500 mt-1 font-mono-meta">{timeLeft(r.trend_expires_at)}</p>
                  )}
                </TableCell>
                <TableCell className="text-sm">{catName(r.category_id)}</TableCell>
                <TableCell className="text-sm">{r.author_name}{r.author_role === "reporter" && <span className="block text-[11px] text-zinc-500">External reporter</span>}</TableCell>
                <TableCell className="text-xs text-zinc-500 font-mono-meta whitespace-nowrap">{fmtDate(r.updated_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
